// After an epoch change: comments and suggestions whose anchor heads the new history doesn't have are re-anchored by
// their quote (FragmentDocument.reanchor), and checkpoints that name states of the replaced history are pointed at the
// same states in the new one (DocumentStore.remapCheckpoints / equivalentHeads, ProjectDocument.remapCheckpointHeads).
import * as A from '@automerge/automerge';
import { currentRange } from '../automerge/anchors.ts';
import { decodeHeads, decodeHeadsIn, encodeHeads } from '../automerge/heads.ts';
import { scalarLength } from '../automerge/offsets.ts';
import { ROOT, SwiftReader } from '../automerge/swiftDoc.ts';
import { changeTime, str, uint, Writer } from '../edit/typed.ts';
import { compactionRecordOf } from './epochID.ts';
import { readTree, sameTree, withoutEpochKeys } from './tree.ts';

// MARK: Quotes

const scalarsOf = (s: string) => [...s];

/** Every place `quote` occurs in `text`, in Unicode scalars (`QuoteSearch.occurrences`). */
export function quoteOccurrences(quote: string, text: string): [number, number][] {
  return find(scalarsOf(quote), scalarsOf(text));
}

function find(needle: string[], hay: string[]): [number, number][] {
  if (!needle.length || needle.length > hay.length) return [];
  const out: [number, number][] = [];
  outer: for (let i = 0; i + needle.length <= hay.length; i++) {
    for (let j = 0; j < needle.length; j++) if (hay[i + j] !== needle[j]) continue outer;
    out.push([i, i + needle.length]);
  }
  return out;
}

/** One scalar folded for matching: case, curly quotes and apostrophes, dashes and odd spaces (one scalar to one). */
function fold(c: string): string {
  if (c === '‘' || c === '’' || c === 'ʼ' || c === '′') return "'";
  if (c === '“' || c === '”' || c === '″') return '"';
  if (c === '–' || c === '—' || c === '‒' || c === '−') return '-';
  if (c === ' ' || c === ' ' || c === ' ' || c === '\t') return ' ';
  const lower = c.toLowerCase();
  return [...lower].length === 1 ? lower : c;
}

/** The occurrence of `quote` nearest `hint` (scalars): exactly, else folded (case, quotes, dashes, spaces). */
export function nearestQuote(quote: string, text: string, hint: number): [number, number] | null {
  const needle = scalarsOf(quote), hay = scalarsOf(text);
  let hits = find(needle, hay);
  if (!hits.length) hits = find(needle.map(fold), hay.map(fold));
  let best: [number, number] | null = null;
  for (const h of hits) if (!best || Math.abs(h[0] - hint) < Math.abs(best[0] - hint)) best = h;
  return best;
}

// MARK: Comments and suggestions

/** Whether `hex` names heads this document doesn't have (an empty or unreadable value counts as known, as the Swift). */
function unknownHeads(hex: string, doc: A.Doc<unknown>): boolean {
  let heads: string[];
  try {
    heads = decodeHeads(hex);
  } catch {
    return hex !== '';
  }
  const b = A.getBackend(doc);
  return heads.length > 0 && heads.some((h) => b.getChangeMetaByHash(h) === null);
}

interface Anchored { id: string; start: number; end: number; heads: string; quote: string }

function anchoredIn(doc: A.Doc<unknown>, key: 'comments' | 'suggestions'): Anchored[] {
  const r = new SwiftReader(doc);
  const all = r.get(ROOT, key);
  if (!all || all[0] !== 'map') return [];
  const out: Anchored[] = [];
  for (const [id, v] of r.entries(all[1] as string)) {
    if (v[0] !== 'map') continue;
    const obj = v[1] as string;
    try {
      out.push({ id, start: r.uint(obj, 'anchorStart') ?? 0, end: r.uint(obj, 'anchorEnd') ?? 0, heads: r.string(obj, 'anchorHeads') ?? '', quote: r.string(obj, 'quote') ?? '' });
    } catch {
      // a damaged entry: left alone
    }
  }
  return out;
}

/**
 * `FragmentDocument.reanchor(in:from:)`: every comment and suggestion of `doc` anchored at heads it doesn't have is
 * re-anchored at its current heads, over its quote where found (the occurrence nearest its place in `old`), else at its
 * old place clamped. One change ("Re-anchored comments after compaction"), or the document unchanged.
 */
export function reanchor(doc: A.Doc<unknown>, old: A.Doc<unknown> | null, at = new Date()): A.Doc<unknown> {
  const text = new SwiftReader(doc).string(ROOT, 'text') ?? '';
  const length = scalarLength(text);
  const heads = encodeHeads(A.getHeads(doc));
  const moves: { key: 'comments' | 'suggestions'; id: string; start: number; end: number }[] = [];
  for (const key of ['comments', 'suggestions'] as const) {
    for (const a of anchoredIn(doc, key)) {
      if (!unknownHeads(a.heads, doc)) continue;
      const near = old ? currentRange(old, { anchorStart: a.start, anchorEnd: a.end, anchorHeads: a.heads })?.[0] : undefined;
      const hint = near ?? a.start;
      const found = a.quote ? nearestQuote(a.quote, text, hint) : null;
      if (found) moves.push({ key, id: a.id, start: found[0], end: found[1] });
      else {
        const span = a.end >= a.start ? a.end - a.start : 0;
        const lower = Math.min(hint, length);
        moves.push({ key, id: a.id, start: lower, end: Math.min(length, lower + Math.min(span, length)) });
      }
    }
  }
  if (!moves.length) return doc;
  const r = new SwiftReader(doc);
  return A.change(doc as A.Doc<Record<string, unknown>>, { message: 'Re-anchored comments after compaction', time: changeTime(at) }, (d) => {
    const w = new Writer(d, r);
    for (const m of moves) {
      w.putIfChanged([m.key, m.id], 'anchorStart', uint(m.start));
      w.putIfChanged([m.key, m.id], 'anchorEnd', uint(m.end));
      w.putIfChanged([m.key, m.id], 'anchorHeads', str(heads));
    }
  }) as A.Doc<unknown>;
}

// MARK: Checkpoints

/**
 * `DocumentStore.equivalentHeads`: heads (encoded) in `next` for the state `old` had at `hex` — from `next`'s compaction
 * record, else one of `candidates` (states of `next`) or the state `next` was compacted from, when it reads the same.
 */
export function equivalentHeads(hex: string, old: A.Doc<unknown> | null, next: A.Doc<unknown>, candidates: string[][] = []): string | null {
  const record = compactionRecordOf(next);
  const mapped = record?.headsMap[hex];
  if (mapped) return mapped;
  const heads = old ? decodeHeadsIn(hex, old) : null;
  if (!heads || !old) return null;
  const state = withoutEpochKeys(readTree(old, heads));
  const options = candidates.filter((c) => c.length);
  if (record) {
    const to = decodeHeadsIn(record.toHeads, next);
    if (to) options.push(to);
  }
  for (const option of options) {
    if (!option.every((h) => A.getBackend(next).getChangeMetaByHash(h) !== null)) continue;
    if (sameTree(withoutEpochKeys(readTree(next, option)), state)) return encodeHeads(option);
  }
  return null;
}

/**
 * Resolves a checkpoint's heads for a fragment: as stored when the document has them, else through the fragment's
 * compaction record (the lazy half of the remap, for checkpoints not yet rewritten). Null when neither.
 */
export function checkpointHeadsIn(hex: string, doc: A.Doc<unknown>): string[] | null {
  const direct = decodeHeadsIn(hex, doc);
  if (direct) return direct;
  const mapped = compactionRecordOf(doc)?.headsMap[hex];
  return mapped ? decodeHeadsIn(mapped, doc) : null;
}

/**
 * `ProjectDocument.remapCheckpointHeads(fragmentID:…)`: points every checkpoint's heads for `fragmentId` that `next`
 * lacks at the equivalent state of `next`. One change ("Remapped checkpoints after adopting a compacted history"), or
 * the project unchanged.
 */
export function remapCheckpoints(project: A.Doc<unknown>, fragmentId: string, old: A.Doc<unknown> | null, next: A.Doc<unknown>,
  candidates: string[][] = [], message = 'Remapped checkpoints after adopting a compacted history', at = new Date()): A.Doc<unknown> {
  const r = new SwiftReader(project);
  const all = r.get(ROOT, 'checkpoints');
  if (!all || all[0] !== 'map') return project;
  const updates: { id: string; hex: string }[] = [];
  for (const [id, v] of r.entries(all[1] as string)) {
    if (v[0] !== 'map') continue;
    const fh = r.get(v[1] as string, 'fragmentHeads');
    if (!fh || fh[0] !== 'map') continue;
    const stored = r.get(fh[1] as string, fragmentId);
    if (!stored || stored[0] !== 'str') continue;
    const hex = stored[1] as string;
    if (decodeHeadsIn(hex, next)) continue;
    const mapped = equivalentHeads(hex, old, next, candidates);
    if (mapped && mapped !== hex) updates.push({ id, hex: mapped });
  }
  if (!updates.length) return project;
  return A.change(project as A.Doc<Record<string, unknown>>, { message, time: changeTime(at) }, (d) => {
    const w = new Writer(d, r);
    for (const u of updates) w.put(['checkpoints', u.id, 'fragmentHeads'], fragmentId, str(u.hex));
  }) as A.Doc<unknown>;
}

/** `remapCheckpointProjectHeads`: the checkpoints' own project heads, after the project document changed epoch. */
export function remapCheckpointProjectHeads(project: A.Doc<unknown>, old: A.Doc<unknown> | null, candidates: string[][] = [],
  message = 'Remapped checkpoints after adopting a compacted history', at = new Date()): A.Doc<unknown> {
  const r = new SwiftReader(project);
  const all = r.get(ROOT, 'checkpoints');
  if (!all || all[0] !== 'map') return project;
  const updates: { id: string; hex: string }[] = [];
  for (const [id, v] of r.entries(all[1] as string)) {
    if (v[0] !== 'map') continue;
    const stored = r.get(v[1] as string, 'projectHeads');
    if (!stored || stored[0] !== 'str') continue;
    const hex = stored[1] as string;
    if (decodeHeadsIn(hex, project)) continue;
    const mapped = equivalentHeads(hex, old, project, candidates);
    if (mapped && mapped !== hex) updates.push({ id, hex: mapped });
  }
  if (!updates.length) return project;
  return A.change(project as A.Doc<Record<string, unknown>>, { message, time: changeTime(at) }, (d) => {
    const w = new Writer(d, r);
    for (const u of updates) w.put(['checkpoints', u.id], 'projectHeads', str(u.hex));
  }) as A.Doc<unknown>;
}
